/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import isEmail from 'validator/es/lib/isEmail';
import { FlexRow, FlexCol, TextField, Icon } from 'sarsaparilla';
import { HolderInfoProps, HolderInfoDefaults } from '../props';

export default class HolderInfoForm extends Component {
    static defaultProps = {
        isInternal: false,
        isFieldSales: false,
        skipCustomerInfo: false,
        number: 1,
        holderInfo: HolderInfoDefaults,
    };

    static propTypes = {
        holderInfo: HolderInfoProps,
        isInternal: PropTypes.bool,
        isFieldSales: PropTypes.bool,
        skipCustomerInfo: PropTypes.bool,
        number: PropTypes.number,
        onChange: PropTypes.func,
        submitAttempted: PropTypes.bool,
        passType: PropTypes.object,
    };

    constructor(props) {
        super(props);
        const {
            holderInfo: { email: confirmation },
        } = this.props;
        this.state = { confirmation };
    }

    componentDidMount() {
        this.props.onChange(
            this.props.holderInfo,
            this.formIsValid(this.props.holderInfo)
        );
    }

    handleFieldChange = (field, { target: { value } }) => {
        const holderInfo = { ...this.props.holderInfo, [field]: value };
        this.props.onChange(holderInfo, this.formIsValid(holderInfo));
    };

    handleEmailConfirmationChange = ({ target: { value: confirmation } }) => {
        const holderInfo = { ...this.props.holderInfo, email_confirmation: confirmation };
        this.props.onChange(holderInfo, this.formIsValid(holderInfo));
        this.setState({ confirmation });
    };

    validateEmail = (value) => ({
        isValid: isEmail(value),
        formattedValue: null,
        errorText: 'Please provide a valid email address',
    });

    validateConfirmationEmail = (confirmation) => ({
        isValid: this.emailsMatch(this.props.holderInfo.email, confirmation),
        formattedValue: null,
        errorText: 'Email and confirmation do not match.',
    });

    emailsMatch = (email, confirmation) => email && email === confirmation;

    formIsValid(holderInfo = HolderInfoDefaults) {
        const { confirmation } = this.state;
        const { first_name: fn, last_name: ln, email } = holderInfo;

        return !!(
            fn &&
            ln &&
            email &&
            this.validateEmail(email).isValid &&
            this.emailsMatch(email, confirmation)
        );
    }

    render() {
        const {
            isInternal,
            holderInfo,
            submitAttempted,
            number,
            isFieldSales,
            passType,
            skipCustomerInfo,
        } = this.props;
        const { confirmation } = this.state;
        const hasZipCode = !passType?.features?.['mailing-info'];

        return (
            <div>
                <FlexRow>
                    <FlexCol xl="auto">
                        <div className="rec-form-item-wrap">
                            <TextField
                                label="First Name"
                                id={`holder-first-name-${number}`}
                                data-testid={`holder-first-name-${number}`}
                                value={skipCustomerInfo ? '' : holderInfo.first_name}
                                isRequired={!skipCustomerInfo}
                                isDisabled={
                                    skipCustomerInfo ||
                                    !!(isFieldSales && holderInfo.first_name)
                                }
                                shouldValidateOnUpdate={submitAttempted}
                                onChange={this.handleFieldChange.bind(this, 'first_name')}
                            />
                        </div>
                    </FlexCol>

                    <FlexCol xl="auto">
                        <div className="rec-form-item-wrap">
                            <TextField
                                label="Last Name"
                                id={`holder-last-name-${number}`}
                                data-testid={`holder-last-name-${number}`}
                                value={skipCustomerInfo ? '' : holderInfo.last_name}
                                isRequired={!skipCustomerInfo}
                                isDisabled={
                                    skipCustomerInfo ||
                                    !!(isFieldSales && holderInfo.last_name)
                                }
                                shouldValidateOnUpdate={submitAttempted}
                                onChange={this.handleFieldChange.bind(this, 'last_name')}
                            />
                        </div>
                    </FlexCol>
                </FlexRow>

                <FlexRow>
                    <FlexCol xl="auto">
                        <div className="rec-form-item-wrap">
                            <TextField
                                type="email"
                                label="Email Address"
                                id={`holder-email-${number}`}
                                value={skipCustomerInfo ? '' : holderInfo.email}
                                isRequired={!skipCustomerInfo}
                                isDisabled={
                                    skipCustomerInfo ||
                                    !!(isFieldSales && holderInfo.email)
                                }
                                validator={this.validateEmail}
                                shouldValidateOnUpdate={submitAttempted}
                                onChange={this.handleFieldChange.bind(this, 'email')}
                            />
                        </div>
                    </FlexCol>

                    <FlexCol xl="auto" className="activitypass-email-confirmation">
                        <div className="rec-form-item-wrap">
                            {!isInternal && (
                                <TextField
                                    type="email"
                                    label="Re-Enter Email Address"
                                    id={`holder-email-confirmation-${number}`}
                                    data-testid={`holder-email-confirmation-${number}`}
                                    value={skipCustomerInfo ? '' : confirmation}
                                    isRequired={!skipCustomerInfo}
                                    isDisabled={
                                        skipCustomerInfo ||
                                        !!(isFieldSales && holderInfo.email)
                                    }
                                    validator={this.validateConfirmationEmail}
                                    shouldValidateOnUpdate={submitAttempted}
                                    onChange={this.handleEmailConfirmationChange}
                                />
                            )}
                        </div>

                        {!isInternal &&
                            this.emailsMatch(holderInfo.email, confirmation) && (
                                <span className="activitypass-email-confirmed">
                                    <Icon iconName="check" />
                                </span>
                            )}
                    </FlexCol>
                </FlexRow>

                {hasZipCode && (
                    <FlexRow>
                        <FlexCol xl={6}>
                            <div className="rec-form-item-wrap">
                                <TextField
                                    id={`zip-code-${number}`}
                                    value={skipCustomerInfo ? '' : holderInfo?.zip_code}
                                    label="ZIP Code"
                                    isRequired={!skipCustomerInfo}
                                    isDisabled={skipCustomerInfo}
                                    shouldValidateOnUpdate={submitAttempted}
                                    onChange={this.handleFieldChange.bind(
                                        this,
                                        'zip_code'
                                    )}
                                />
                            </div>
                        </FlexCol>
                    </FlexRow>
                )}
            </div>
        );
    }
}

// cspell:ignore testid, activitypass
